import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Components
import { Header, PDFViewer, BookmarkTabs, UpgradeModal } from './index';

// Utilities
import { renderPage, getCanvasCoordinates, drawRedactionBox } from '../utils/pdfUtils';
import {
  fetchUserFiles,
  uploadFile,
  deleteFile,
  saveRedactions,
  fetchPatterns,
  fetchBillingUsage,
  createCheckoutSession,
} from '../utils/apiUtils';

// Tiny valid 1-page PDF ("Sample Redacto File") in base64 to preload as demo
const SAMPLE_PDF_BASE64 =
  'JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlIC9DYXRhbG9nL1BhZ2VzIDIgMCBSPj4KZW5kb2JqCjIgMCBvYmoKPDwvVHlwZSAvUGFnZXMvS2lkcyBbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoKMyAwIG9iago8PC9UeXBlIC9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveCBbMCAwIDYxMiA3OTJdL0NvbnRlbnRzIDQgMCBSPj4KZW5kb2JqCjQgMCBvYmoKPDwvTGVuZ3RoIDQ5Pj4Kc3RyZWFtCkJUIAovRjEgMjQgVGYKMTUwIDc0MCBUZAooSGVsbG8gV29ybGQhKSBUVAplbmRzdHJlYW0KZW5kb2JqCnhyZWYKMAo1CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDEwMSAwMDAwMCBuIAowMDAwMDAwMDU1IDAwMDAwIG4gCjAwMDAwMDAxNTQgMDAwMDAgbiAKMDAwMDAwMDI5MiAwMDAwMCBuIAp0cmFpbGVyCjw8L1Jvb3QgMSAwIFIvU2l6ZSA1Pj4Kc3RhcnR4cmVmCjM5NQolJUVPRgo=';

const HIDDEN_SEEDED_PATTERNS = new Set([
  'dates',
  'patient_names',
  'names',
  'case_numbers',
  'mrn',
  'agency_contacts',
  'addresses',
]);

function RedactionApp({ user, onLogout, theme = 'dark', toggleTheme }) {
  // Constants
  const STATE_NOTHING = 0;
  const STATE_PDF_DISPLAYED = 1;

  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const stateDataRef = useRef({ redactions: {} });

  const [state, setState] = useState(STATE_NOTHING);
  const [stateData, setStateData] = useState({ redactions: {} });
  const [files, setFiles] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [isRedactionMode, setIsRedactionMode] = useState(false);
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false);
  const [patternsDrawerOpen, setPatternsDrawerOpen] = useState(false);
  const [currentHistoryNode, setCurrentHistoryNode] = useState(null);
  const [usage, setUsage] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [modalMode, setModalMode] = useState('upgrade');
  const [preset, setPreset] = useState(null);
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const PRESET_DETAILS = {
    legal: 'Names, addresses, case numbers, dates, signatures.',
    healthcare: 'PHI: patient names, MRN, DOB, phone, addresses, clinician names.',
    hr: 'Employee names, phone, personal email, salary figures, IDs.',
    gov: 'ID numbers, addresses, agency contact info, classified markers.',
  };
  const PRESET_PATTERN_MAP = {
    legal: ['names', 'addresses', 'case_numbers', 'dates'],
    healthcare: ['patient_names', 'mrn', 'dob', 'phone_numbers'],
    hr: ['employee_names', 'personal_email', 'salary'],
    gov: ['id_numbers', 'agency_contacts', 'classified_markers'],
  };

  const handleApplyPreset = async () => {
    if (!preset) return;
    try {
      const wanted = PRESET_PATTERN_MAP[preset] || [];
      await fetch('/api/patterns/ensure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patterns: wanted }),
      });
      const available = await fetchPatterns();
      const present = wanted.filter((w) => available.includes(w));
      setPatternsDrawerOpen(true);
      if (present.length === 0) {
        alert('Preset loaded. Patterns created, but none found after refresh—please try again.');
      } else {
        alert(`Preset ready. Found ${present.length} patterns. Applying to the open PDF...`);
        if (stateData.currentPdfId && stateData.pdfPath && present.length) {
          await applyPatternsToCurrent(present);
        }
      }
    } catch (e) {
      console.error('Preset load failed', e);
      alert('Could not load preset patterns right now.');
    }
  };
  const TOUR_STEPS = [
    {
      title: '1) Drop a PDF',
      body: 'Upload a PDF in the left panel. It is encrypted on receipt and prepped for redaction.',
    },
    {
      title: '2) Apply patterns',
      body: 'Use saved patterns or create new ones on the right to auto-select sensitive areas.',
    },
    {
      title: '3) Review & export',
      body: 'Preview pages in the center, then export a redacted PDF. Every action is audit-logged.',
    },
    {
      title: '4) See history',
      body: 'Open the Dashboard for stats and the full audit log. Download CSV if needed.',
    },
  ];
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [hotspot, setHotspot] = useState(null);
  const [toast, setToast] = useState(null);

  const totalFiles = files.length;
  const todayActions = logs.filter((log) => {
    const d = new Date(log.ts || log.timestamp || log.created_at || Date.now());
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }).length;

  useEffect(() => {
    stateDataRef.current = stateData;
  }, [stateData]);

  const showPDF = useCallback((file) => {
    console.log('Showing PDF:', file.path, 'with', file.pages, 'pages');
    if (!file.path) {
      alert('No PDF loaded.');
      return;
    }
    let userIdFromPath = null;
    const pathParts = file.path.split('/');
    const rootIndex = pathParts.findIndex(part => part === 'pdf' || part === 'redacted');
    if (rootIndex !== -1 && pathParts.length > rootIndex + 1) {
      userIdFromPath = pathParts[rootIndex + 1];
    } else if (pathParts.length >= 2) {
      userIdFromPath = pathParts[0];
    }
    setState(STATE_PDF_DISPLAYED);
    setStateData((prevState) => ({
      ...prevState,
      pdfPath: file.path,
      currentPdfId: file.id,
      redactions: {},
      pathWithoutExt: file.path,
      userId: userIdFromPath,
    }));
    setTotalPages(file.pages);
    setCurrentPage(0);
    setCurrentHistoryNode({
      redactions: {},
      previous: null,
      next: null
    });
  }, []);

  useEffect(() => {
    if (!location.state?.fileId || files.length === 0) {
      return;
    }
    const uploadedFile = files.find((file) => String(file.id) === String(location.state.fileId));
    if (uploadedFile) {
      showPDF(uploadedFile);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [files, location.state, showPDF]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = overlayCanvasRef.current;
    if (canvas && overlay) {
      overlay.width = canvas.width;
      overlay.height = canvas.height;
      
      // Sync CSS dimensions to match display size
      overlay.style.width = canvas.style.width || `${canvas.width}px`;
      overlay.style.height = canvas.style.height || `${canvas.height}px`;
    }
  }, []);

  // Update overlay canvas size and position when main canvas changes
  useEffect(() => {
    const mainCanvas = canvasRef.current;
    const overlay = overlayCanvasRef.current;
    if (mainCanvas && overlay) {
      overlay.width = mainCanvas.width;
      overlay.height = mainCanvas.height;
      
      overlay.style.width = mainCanvas.style.width || `${mainCanvas.width}px`;
      overlay.style.height = mainCanvas.style.height || `${mainCanvas.height}px`;
    }
  }, [currentPage]); 

  useEffect(() => {
    if (state === STATE_PDF_DISPLAYED && stateData.pathWithoutExt) {
      console.log('Rendering PDF page:', currentPage, 'Path:', stateData.pathWithoutExt);
      renderPage(currentPage, stateData.pathWithoutExt, canvasRef, stateDataRef.current);
    }
  }, [currentPage, state, stateData.pathWithoutExt]); 

  useEffect(() => {
    return () => {
    };
  }, []);

  const fetchFiles = useCallback(async () => {
    console.log('📁 Fetching user files...');
    const nextFiles = await fetchUserFiles();
    console.log('📁 Files fetched:', nextFiles.length, 'files');
    setFiles(nextFiles);
  }, []);

  const fetchPatternsData = useCallback(async () => {
    const nextPatterns = await fetchPatterns();
    setPatterns((nextPatterns || []).filter((p) => !HIDDEN_SEEDED_PATTERNS.has(p)));
  }, []);

  const loadUsage = useCallback(async () => {
    const result = await fetchBillingUsage();
    if (result.success) {
      setUsage(result.data);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const billingStatus = params.get('billing');

    if (billingStatus === 'success') {
      loadUsage();
      alert('Upgrade successful. Your plan is now active.');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (billingStatus === 'cancelled') {
      alert('Checkout was cancelled. You can upgrade any time from the editor.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, loadUsage]);

  useEffect(() => {
    fetchFiles();
    fetchPatternsData();
    loadUsage();
  }, [user, fetchFiles, fetchPatternsData, loadUsage]);

  useEffect(() => {
    if (location.state?.preset) {
      setPreset(location.state.preset);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  const handleStartTour = () => {
    setTourStep(0);
    setShowTour(true);
    setHotspot('upload');
  };

  const handleNextTour = () => {
    setTourStep((s) => {
      const next = Math.min(s + 1, TOUR_STEPS.length - 1);
      setHotspot(['upload','patterns','export','dashboard'][next] || null);
      return next;
    });
  };

  const handlePrevTour = () => {
    setTourStep((s) => {
      const prev = Math.max(s - 1, 0);
      setHotspot(['upload','patterns','export','dashboard'][prev] || null);
      return prev;
    });
  };

  const handleCloseTour = () => { setShowTour(false); setHotspot(null); };

  const handleLoadSample = async () => {
    try {
      setIsLoading(true);
      const byteCharacters = atob(SAMPLE_PDF_BASE64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const file = new File([blob], 'Sample_Redacto_File.pdf', { type: 'application/pdf' });

      const formData = new FormData();
      formData.append('file', file);

      const result = await uploadFile(formData);
      if (result.success) {
        await fetchFiles();
        const sample = (files || []).find((f) => (f.title || '').includes('Sample_Redacto_File'));
        if (sample) {
          showPDF(sample);
        }
      } else {
        alert(result.error || 'Failed to load sample file.');
      }
    } catch (err) {
      console.error('Sample load failed:', err);
      alert('Failed to load sample file.');
    }
    setIsLoading(false);
  };

  const applyPatternsToCurrent = async (patternNames = []) => {
    if (!patternNames.length || !stateData.currentPdfId || !stateData.pdfPath) return;
    setIsLoading(true);
    try {
      const res = await fetch(`/api/patterns/${patternNames.join(',')}/redact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdfPath: stateData.pdfPath, file_id: stateData.currentPdfId }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || res.statusText || 'Unable to apply patterns');
      }
      const refreshed = await fetchUserFiles();
      setFiles(refreshed);
      const match = data?.id
        ? refreshed.find((f) => String(f.id) === String(data.id))
        : refreshed.find((f) => f.title === data?.filename);
      if (match) {
        showPDF(match);
      }
      setToast({ type: 'success', message: 'Preset patterns applied. Opened the redacted file.' });
    } catch (err) {
      console.error('applyPatternsToCurrent error', err);
      setToast({ type: 'error', message: `Could not apply preset patterns right now. ${err.message || ''}` });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await fetch('/api/logs');
        if (!res.ok) throw new Error('');
        const data = await res.json();
        const normalized = (Array.isArray(data?.logs) ? data.logs : []).map((log) => ({
          ...log,
          file: log.file || log.file_name || log.file_id,
          ts: log.ts || log.created_at || log.timestamp,
          user: log.user || log.username,
        }));
        setLogs(normalized);
      } catch (e) {
        setLogs([
          { id: 1, ts: new Date().toISOString(), action: 'upload', file: 'Sample_Redacto_File.pdf', user: user?.login || 'me' },
          { id: 2, ts: new Date().toISOString(), action: 'redact', file: 'Sample_Redacto_File.pdf', user: user?.login || 'me' },
        ]);
      }
    };
    fetchLogs();
  }, [user]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    let formData = new FormData();
    
    if (e.target.tagName === 'FORM') {
      formData = new FormData(e.target);
    } else if (e.target.files && e.target.files.length > 0) {
      formData.append('file', e.target.files[0]);
    } else if (e.target.elements && e.target.elements.file) {
      formData.append('file', e.target.elements.file.files[0]);
    }
    
    const result = await uploadFile(formData);
    
    if (result.success) {
      await fetchFiles();
      // Only reset if it's a form
      if (e.target.tagName === 'FORM') {
        e.target.reset();
      } else if (e.target.type === 'file') {
        e.target.value = '';
      }
    } else {
      alert('Upload failed: ' + result.error);
    }
    setIsLoading(false);
  };

  const handleRedactStart = () => {
    if (state !== STATE_PDF_DISPLAYED) {
      alert('Select a file first.');
      return;
    }
    setIsRedactionMode(prev => !prev); // Toggle redaction mode
  };

  const handleMouseDown = (event) => {
    console.log('Mouse down - Redaction mode:', isRedactionMode);
    if (!isRedactionMode) return;
    
    const coords = getCanvasCoordinates(event, canvasRef, stateDataRef.current);
    console.log('Mouse down coordinates:', coords);
    if (!coords) return;

    setIsDrawing(true);
    setStartPoint(coords);
  };

  const handleMouseMove = (event) => {
    if (!isDrawing || !startPoint || !isRedactionMode) return;

    const coords = getCanvasCoordinates(event, canvasRef, stateDataRef.current);
    if (!coords) return;

    // Use the drawRedactionBox utility for preview
    drawRedactionBox(startPoint, coords, overlayCanvasRef, canvasRef, stateData, true);
  };

  const handleMouseUp = (event) => {
    if (!isDrawing || !startPoint || !isRedactionMode) return;

    const coords = getCanvasCoordinates(event, canvasRef, stateDataRef.current);
    if (!coords) return;

    // Calculate final redaction coordinates
    const x1 = Math.min(startPoint.x, coords.x);
    const y1 = Math.min(startPoint.y, coords.y);
    const x2 = Math.max(startPoint.x, coords.x);
    const y2 = Math.max(startPoint.y, coords.y);
    const width = x2 - x1;
    const height = y2 - y1;
    console.log('x1', x1, 'x2', x2, 'y1', y1, 'y2', y2)

    // Only add redaction if it has significant size
    if (width > 0.005 && height > 0.005) {
      const newRedaction = {
        x: [x1, x2],
        y: [y1, y2]
      };

      // Update state with proper page-based redaction structure
      const newRedactions = { ...stateData.redactions };
      if (!newRedactions[currentPage]) {
        newRedactions[currentPage] = [];
      }
      newRedactions[currentPage].push(newRedaction);

      // Create new history node
      const newHistoryNode = {
        redactions: newRedactions,
        previous: currentHistoryNode,
        next: null
      };

      // Update previous node's next pointer
      if (currentHistoryNode) {
        currentHistoryNode.next = newHistoryNode;
      }

      setCurrentHistoryNode(newHistoryNode);
      setStateData(prevState => ({
        ...prevState,
        redactions: newRedactions
      }));

      // Re-render the PDF with new redactions
      renderPage(currentPage, stateData.pathWithoutExt, canvasRef, { ...stateData, redactions: newRedactions });
    }

    // Clear overlay
    const overlay = overlayCanvasRef.current;
    if (overlay) {
      const ctx = overlay.getContext('2d');
      ctx.clearRect(0, 0, overlay.width, overlay.height);
    }

    setIsDrawing(false);
    setStartPoint(null);
  };

  const handleUndo = () => {
    if (currentHistoryNode && currentHistoryNode.previous) {
      setCurrentHistoryNode(currentHistoryNode.previous);
      setStateData(prevState => ({
        ...prevState,
        redactions: currentHistoryNode.previous.redactions
      }));
      renderPage(currentPage, stateData.pathWithoutExt, canvasRef, { ...stateData, redactions: currentHistoryNode.previous.redactions });
    }
  };

  const handleRedo = () => {
    if (currentHistoryNode && currentHistoryNode.next) {
      setCurrentHistoryNode(currentHistoryNode.next);
      setStateData(prevState => ({
        ...prevState,
        redactions: currentHistoryNode.next.redactions
      }));
      renderPage(currentPage, stateData.pathWithoutExt, canvasRef, { ...stateData, redactions: currentHistoryNode.next.redactions });
    }
  };

  const handleMouseLeave = () => {
    if (isDrawing) {
      // Clear overlay when mouse leaves
      const overlay = overlayCanvasRef.current;
      if (overlay) {
        const ctx = overlay.getContext('2d');
        ctx.clearRect(0, 0, overlay.width, overlay.height);
      }
      setIsDrawing(false);
      setStartPoint(null);
    }
  };

  const savePDF = async () => {
    if (state !== STATE_PDF_DISPLAYED) {
      setToast({ type: 'error', message: 'No PDF loaded.' });
      return;
    }

    setIsLoading(true);
    try {
      // Convert page-based redactions to the format expected by the backend
      const redactionsForBackend = {};
      Object.keys(stateData.redactions).forEach(pageNum => {
        const pageRedactions = stateData.redactions[pageNum];
        if (pageRedactions && pageRedactions.length > 0) {
          redactionsForBackend[pageNum] = pageRedactions;
        }
      });

      const result = await saveRedactions(stateData.currentPdfId, redactionsForBackend);
      if (result.success) {
        if (result.usage) {
          setUsage(result.usage);
        }
        setToast({ type: 'success', message: 'PDF saved successfully.' });
        if (result.warnings && result.warnings.length) {
          console.warn('Redaction warnings:', result.warnings);
          setToast({ type: 'warn', message: `Saved with warnings on ${result.warnings.length} page(s)` });
        }
        // Force-download using fetch + blob so the file is reliably downloaded on Save.
        const downloadHref = result.download_url.startsWith('http')
          ? result.download_url
          : new URL(result.download_url, window.location.origin).toString();
        const downloadResponse = await fetch(downloadHref, {
          method: 'GET',
          credentials: 'include',
        });
        if (!downloadResponse.ok) {
          throw new Error(`Download failed (HTTP ${downloadResponse.status})`);
        }

        const pdfBlob = await downloadResponse.blob();
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = result.filename || 'redacted.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        if (result.warnings && result.warnings.length) {
          const details = result.warnings.join(' | ');
          setToast({ type: 'error', message: `Redaction failed: ${details}` });
          return;
        }
        if (result.usage) {
          setUsage(result.usage);
        }
        if (result.loginRequired) {
          openUpgradeModal(
            result.error || 'You have used all 10 free guest redactions. Continue with Google to keep redacting files.',
            'login'
          );
        } else if (result.upgradeRequired) {
          openUpgradeModal(result.error || 'You have reached your free redaction limit. Upgrade to continue.');
        } else {
          setToast({ type: 'error', message: 'Failed to save PDF: ' + (result.error || '') });
        }
      }
    } catch (error) {
      console.error('Error saving PDF:', error);
      setToast({ type: 'error', message: 'Failed to save PDF' });
    }
    setIsLoading(false);
  };

  const changePage = (delta) => {
    const newPage = currentPage + delta;
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleDelete = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      const result = await deleteFile(fileId);
      if (result.success) {
        await fetchFiles();
        if (stateData.currentPdfId === fileId) {
          setState(STATE_NOTHING);
          setStateData({ redactions: {} });
        }
      } else {
        alert('Failed to delete file: ' + result.error);
      }
    }
  };

  const openUpgradeModal = useCallback((message = '', mode = 'upgrade') => {
    setModalMode(mode);
    setUpgradeMessage(message);
    setCheckoutError('');
    setUpgradeModalOpen(true);
  }, []);

  const handleUpgradeCheckout = useCallback(async () => {
    if (modalMode === 'login') {
      navigate('/login');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError('');
    const result = await createCheckoutSession(window.location.origin);
    if (result.success && result.data?.checkout_url) {
      window.location.href = result.data.checkout_url;
      return;
    }
    setCheckoutError(result.error || 'Unable to start checkout right now.');
    setCheckoutLoading(false);
  }, [modalMode, navigate]);

  return (
    <React.Fragment>
      <div className="redaction-shell">
        <Header user={user} handleLogout={onLogout} theme={theme} toggleTheme={toggleTheme} usage={usage} />
        <div className="editor-bar">
          <div className="editor-bar-left">
            <span className="editor-title">Redaction console</span>
            <button type="button" className="editor-btn" onClick={() => setSessionsDrawerOpen(true)}>Sessions</button>
            <button type="button" className="editor-btn" onClick={() => setPatternsDrawerOpen(true)}>Patterns</button>
            <button type="button" className="editor-btn" onClick={() => setLogDrawerOpen(true)}>Activity log</button>
            <button type="button" className="editor-btn ghost" onClick={() => navigate('/dashboard')}>Dashboard</button>
          </div>
          <div className="editor-bar-right">
            <button type="button" className={`editor-btn primary ${hotspot === 'upload' ? 'hotspot' : ''}`} onClick={handleLoadSample}>
              Load sample
            </button>
            <button type="button" className={`editor-btn ${hotspot === 'tour' ? 'hotspot' : ''}`} onClick={handleStartTour}>
              Start tour
            </button>
            <div className="editor-metric">
              <span>Files</span>
              <strong>{totalFiles}</strong>
            </div>
            <div className="editor-metric">
              <span>Actions</span>
              <strong>{todayActions}</strong>
            </div>
          </div>
        </div>

        {preset && (
          <div className="editor-preset-inline">
            <div>
              <div className="preset-label">Preset active: {preset.charAt(0).toUpperCase() + preset.slice(1)}</div>
              <div className="preset-copy">{PRESET_DETAILS[preset] || 'Pattern suggestions loaded.'}</div>
            </div>
            <div className="preset-actions">
              <button className="qs-btn" type="button" onClick={handleApplyPreset}>Load suggestions</button>
              <button className="qs-btn ghost" type="button" onClick={() => setPreset(null)}>Clear</button>
            </div>
          </div>
        )}

        <div className="main-content">
          {/* Main content area with viewer and tabs */}
          <div className="pdf-content-area">
          <BookmarkTabs 
            files={files} 
            patterns={patterns}
            isLoading={isLoading} 
            handleUpload={handleUpload} 
            showPDF={showPDF}
            handleDelete={handleDelete}
            currentPdfId={stateData.currentPdfId}
            currentPage={currentPage}
            stateData={stateData} 
            setSessionsDrawerOpen={setSessionsDrawerOpen}
            setPatternsDrawerOpen={setPatternsDrawerOpen}
            fetchPatternsData={fetchPatternsData}
            sessionsDrawerOpen={sessionsDrawerOpen}
            patternsDrawerOpen={patternsDrawerOpen}
          />
          
          <PDFViewer
            state={state}
            stateData={stateData}
            currentPage={currentPage}
            totalPages={totalPages}
            changePage={changePage}
            isRedactionMode={isRedactionMode}
            handleRedactStart={handleRedactStart}
            savePDF={savePDF}
            isLoading={isLoading}
            canvasRef={canvasRef}
            overlayCanvasRef={overlayCanvasRef}
            handleMouseDown={handleMouseDown}
            handleMouseMove={handleMouseMove}
            handleMouseUp={handleMouseUp}
            handleMouseLeave={handleMouseLeave}
            handleUndo={handleUndo}
            handleRedo={handleRedo}
            currentHistoryNode={currentHistoryNode}
            STATE_NOTHING={STATE_NOTHING}
            STATE_PDF_DISPLAYED={STATE_PDF_DISPLAYED}
            sessionsDrawerOpen={sessionsDrawerOpen}
            setSessionsDrawerOpen={setSessionsDrawerOpen}
            patternsDrawerOpen={patternsDrawerOpen}
            setPatternsDrawerOpen={setPatternsDrawerOpen}
            files={files}
            patterns={patterns}
            handleUpload={handleUpload}
            showPDF={showPDF}
            handleDelete={handleDelete}
            fetchPatternsData={fetchPatternsData}
            setFiles={setFiles}
            hotspot={hotspot}
            setToast={setToast}
          />
          </div>
        </div>
      </div>

      {showTour && (
        <div className="tour-overlay" role="dialog" aria-modal="true">
          <div className="tour-card">
            <div className="tour-header">
              <span className="tour-pill">60s tour</span>
              <button className="tour-close" onClick={handleCloseTour} aria-label="Close tour">×</button>
            </div>
            <div className="tour-step-count">Step {tourStep + 1} / {TOUR_STEPS.length}</div>
            <h3>{TOUR_STEPS[tourStep].title}</h3>
            <p>{TOUR_STEPS[tourStep].body}</p>
            <div className="tour-actions">
              <button className="qs-btn ghost" onClick={handleCloseTour}>Skip</button>
              <div className="tour-nav">
                <button className="qs-btn" onClick={handlePrevTour} disabled={tourStep === 0}>Prev</button>
                {tourStep === TOUR_STEPS.length - 1 ? (
                  <button className="qs-btn" onClick={handleCloseTour}>Done</button>
                ) : (
                  <button className="qs-btn" onClick={handleNextTour}>Next</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast toast-${toast.type || 'info'}`}>
          <span>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <UpgradeModal
        open={upgradeModalOpen}
        usage={usage}
        message={upgradeMessage}
        loading={checkoutLoading}
        checkoutError={checkoutError}
        badge={modalMode === 'login' ? 'Guest Trial Complete' : 'Upgrade Required'}
        title={modalMode === 'login' ? 'Continue with Google to keep redacting' : 'You reached your free redaction limit'}
        actionLabel={modalMode === 'login' ? 'Continue with Google' : 'Upgrade with Stripe'}
        secondaryLabel={modalMode === 'login' ? 'Stay in guest mode' : 'Maybe later'}
        onClose={() => {
          if (!checkoutLoading) {
            setUpgradeModalOpen(false);
            setCheckoutError('');
            setModalMode('upgrade');
          }
        }}
        onUpgrade={handleUpgradeCheckout}
      />
      {logDrawerOpen && (
        <div className="log-drawer" role="dialog" aria-label="Activity log">
          <div className="log-drawer-header">
            <strong>Activity log</strong>
            <button type="button" onClick={() => setLogDrawerOpen(false)}>×</button>
          </div>
          <div className="log-drawer-body">
            {logs.length === 0 ? (
              <p className="log-empty">No activity yet.</p>
            ) : (
              <ul>
                {logs.map((log) => (
                  <li key={log.id || `${log.ts}-${log.action}`}>
                    <div className="log-line">
                      <span className="log-action">{log.action}</span>
                      <span className="log-file">{log.file || '—'}</span>
                    </div>
                    <div className="log-meta">{log.user || 'user'} • {new Date(log.ts).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
export default RedactionApp; 
